import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { api, setToken, clearToken, getToken } from '@/lib/api'

export interface User {
  id: string
  username: string
  displayName: string
  avatarUrl?: string
  email?: string
  connections?: Connection[]
}

export interface Connection {
  id: string
  platform: string
  platformUsername: string
  connectedAt: string
}

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (token: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchMe = useCallback(async () => {
    try {
      const me = await api.get<User>('/me')
      setUser(me)
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const token = getToken()
    if (token) {
      fetchMe()
    } else {
      setIsLoading(false)
    }
  }, [fetchMe])

  // Cross-tab logout / login sync via localStorage events.
  // When another tab removes the token, log out here too.
  // When another tab updates the token (e.g. login), re-fetch the user.
  useEffect(() => {
    const TOKEN_KEY = 'kast_token'
    function handleStorage(e: StorageEvent) {
      if (e.key !== TOKEN_KEY) return
      if (e.newValue === null) {
        // Token was removed in another tab — log out locally
        setUser(null)
      } else if (e.newValue && e.newValue !== e.oldValue) {
        // Token was updated in another tab — re-fetch user
        fetchMe()
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [fetchMe])

  const login = useCallback(async (token: string) => {
    setToken(token)
    try {
      const me = await api.get<User>('/me')
      setUser(me)
      setIsLoading(false)
    } catch (err) {
      setUser(null)
      setIsLoading(false)
      clearToken()
      throw err
    }
  }, [])

  const logout = useCallback(() => {
    clearToken()
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
